import { ArrowRightIcon } from 'lucide-react'

import { Badge, BorderedRadioButton, Button, RadioGroup } from 'slab'

import { emitEmployerOnboardingEvent } from '../../utils'
import { useSelfServeExplorerContext } from '../QuoteSelfServeExplorerContext'
import { AllowanceStrategies, type AllowanceStrategy } from '../types'

const CARRIER_LOGO_URL = '/assets/carrier-icons/blueshield-of-california-022120f6.svg'

interface EmployeeExampleProps {
  location: string
  age: number
  dependents: number
  allowance: number
  planCost: number
  maxValue: number
}

function EmployeeExample ({ location, age, dependents, allowance, planCost, maxValue }: EmployeeExampleProps) {
  const cost = Math.max(0, planCost - allowance)

  const allowanceBarWidth = (allowance / maxValue) * 100
  const planCostBarWidth = (planCost / maxValue) * 100

  return (
    <div className='flex items-center gap-4'>
      <div className='flex flex-col gap-1 body-sm text-gray-500 w-28 shrink-0'>
        <span>{location}</span>
        <span>{age} years old</span>
        <span>{dependents} dependent{dependents !== 1 ? 's' : ''}</span>
      </div>
      <div className='flex-1 flex flex-col gap-2'>
        <div className='h-7 rounded-lg' style={{ width: '303px' }}>
          <div
            className='h-full bg-thatch-300 rounded-lg flex items-center gap-2'
            style={{ width: `${allowanceBarWidth}%`, minWidth: '140px' }}
          >
            <div className='h-full px-2 py-1 bg-white/75 border-2 border-thatch-300 rounded-lg flex items-center w-16'>
              <span className='body-sm text-gray-800'>${Math.round(allowance)}</span>
            </div>
            <span className='body-sm text-gray-800'>Allowance</span>
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <div className='h-7 rounded-lg shrink-0' style={{ width: '303px' }}>
            <div
              className='h-full bg-gray-300 rounded-lg flex items-center gap-2'
              style={{ width: `${planCostBarWidth}%`, minWidth: '140px' }}
            >
              <div className='h-full px-2 py-1 bg-white/75 border-2 border-gray-300 rounded-lg flex items-center gap-1 w-16'>
                <span className='body-sm text-gray-800'>${Math.round(planCost)}</span>
                <img src={CARRIER_LOGO_URL} alt='Carrier logo' className='w-4 h-4 shrink-0' />
              </div>
              <span className='body-sm text-gray-800'>Plan cost</span>
            </div>
          </div>
          <span className={`body-sm-bold ${cost > 0 ? 'text-red-400' : 'text-gray-500'}`}>
            ${Math.round(cost)} cost
          </span>
        </div>
      </div>
    </div>
  )
}

interface AllowanceStrategySelectorProps {
  onNext?: () => void
  onStrategyChange?: () => void
}

// Mock employee data for examples
const MOCK_EMPLOYEE_1 = {
  location: 'San Francisco, CA',
  age: 26,
  dependents: 0
}

const MOCK_EMPLOYEE_2 = {
  location: 'San Francisco, CA',
  age: 39,
  dependents: 2
}

export function AllowanceStrategySelector ({ onNext, onStrategyChange }: AllowanceStrategySelectorProps) {
  const { allowanceStrategy, setAllowanceStrategy } = useSelfServeExplorerContext()

  const handleStrategyChange = (strategy: AllowanceStrategy) => {
    setAllowanceStrategy(strategy)
    emitEmployerOnboardingEvent('[ER] AllowanceTool allowance strategy type toggled', {
      change_to: strategy
    })
    onStrategyChange?.()
  }

  // Example allowance/cost data for demonstration
  const flatAllowance = 600
  const employee1PlanCost = 600
  const employee2PlanCost = 700

  // For smart allowances, the allowance matches the plan cost (covering full cost)
  const isFlat = allowanceStrategy === AllowanceStrategies.Flat

  // Calculate global max for consistent bar proportions
  const employee1Allowance = isFlat ? flatAllowance : employee1PlanCost
  const employee2Allowance = isFlat ? flatAllowance : employee2PlanCost
  const globalMaxValue = Math.max(employee1Allowance, employee2Allowance, employee1PlanCost, employee2PlanCost)

  const exampleDescription = isFlat
    ? 'Both employees get the same monthly allowance'
    : 'Each employee gets an allowance adjusted to their plan costs'

  return (
    <div className='flex flex-col gap-4 p-6'>
      <div className='flex items-center justify-between pb-4 pt-6 px-6 -mx-6 -mt-6'>
        <h2 className='heading-sm text-gray-800'>Allowance strategy</h2>
        {onNext && (
          <Button
            variant='primary'
            size='small'
            onClick={onNext}
            rightIcon={ArrowRightIcon}
          >
            Next
          </Button>
        )}
      </div>

      <RadioGroup name='allowance_strategy' layout='horizontal' gap={3}>
        <div className='flex gap-3 w-full'>
          <div className='flex-1'>
            <BorderedRadioButton
              value={AllowanceStrategies.Flat}
              label='Same amount for everyone'
              description='Each employee gets the same dollar amount for themselves and their dependents.'
              checked={allowanceStrategy === AllowanceStrategies.Flat}
              onChange={() => handleStrategyChange(AllowanceStrategies.Flat)}
              size='large'
            />
          </div>
          <div className='flex-1'>
            <BorderedRadioButton
              value={AllowanceStrategies.Smart}
              label='Smart allowances'
              description='Each employees gets a dollar amount based on their plan costs.'
              checked={allowanceStrategy === AllowanceStrategies.Smart}
              onChange={() => handleStrategyChange(AllowanceStrategies.Smart)}
              size='large'
            />
          </div>
        </div>
      </RadioGroup>

      <div className='bg-gray-50 rounded-lg border border-gray-200'>
        <div className='flex items-center gap-2 p-4 border-b border-gray-200'>
          <Badge type='thatch' bordered>Example</Badge>
          <span className='body text-gray-800'>{exampleDescription}</span>
        </div>

        <div className='flex flex-col gap-6 p-4'>
          <EmployeeExample
            location={MOCK_EMPLOYEE_1.location}
            age={MOCK_EMPLOYEE_1.age}
            dependents={MOCK_EMPLOYEE_1.dependents}
            allowance={employee1Allowance}
            planCost={employee1PlanCost}
            maxValue={globalMaxValue}
          />
          <EmployeeExample
            location={MOCK_EMPLOYEE_2.location}
            age={MOCK_EMPLOYEE_2.age}
            dependents={MOCK_EMPLOYEE_2.dependents}
            allowance={employee2Allowance}
            planCost={employee2PlanCost}
            maxValue={globalMaxValue}
          />
        </div>
      </div>
    </div>
  )
}
